import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ThumbsUp, 
  Plus,
  TrendingUp,
  Clock,
  CheckCircle,
  Loader2,
  ExternalLink,
  Search,
  ArrowUpRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RequestsPage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [votingId, setVotingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    software_name: '',
    description: '',
    use_case: '',
    api_docs_url: ''
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/requests`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please sign in to submit a request');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/requests`, formData);
      toast.success('Integration request submitted!');
      setModalOpen(false);
      setFormData({ software_name: '', description: '', use_case: '', api_docs_url: '' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (requestId) => {
    if (!user) {
      toast.error('Please sign in to vote');
      return;
    }
    
    setVotingId(requestId);
    try {
      await axios.post(`${API}/requests/${requestId}/vote`);
      toast.success('Vote recorded!');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vote');
    } finally {
      setVotingId(null);
    }
  };

  const filteredRequests = requests.filter(req =>
    req.software_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    req.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-950/30 text-blue-400 border-blue-900/50',
      in_progress: 'bg-amber-950/30 text-amber-400 border-amber-900/50',
      completed: 'bg-emerald-950/30 text-emerald-400 border-emerald-900/50',
    };
    return styles[status] || 'bg-slate-800 text-slate-300 border-slate-700';
  };

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
          <div>
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/30">
              <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
              Community Driven
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
              Integration Requests
            </h1>
            <p className="text-lg text-slate-400">
              Vote for integrations you want to see. Most voted = built first!
            </p>
          </div>
          <Button 
            onClick={() => setModalOpen(true)}
            className="glow-primary bg-primary hover:bg-primary/90"
            data-testid="request-integration-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Request Integration
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            placeholder="Search requests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 bg-secondary border-border"
            data-testid="search-requests-input"
          />
        </div>

        {/* Requests List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 rounded-full bg-secondary mx-auto mb-6 flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-white mb-2">No requests yet</h3>
            <p className="text-slate-400 mb-6">Be the first to request an integration!</p>
            <Button onClick={() => setModalOpen(true)} className="bg-primary hover:bg-primary/90">
              Request Integration
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request, index) => (
              <div 
                key={request.id}
                className="bg-card border border-white/5 rounded-xl p-6 hover:border-white/10 transition-colors"
                data-testid={`request-card-${request.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Vote Button */}
                  <button
                    onClick={() => handleVote(request.id)}
                    disabled={votingId === request.id}
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
                      request.voted_by?.includes(user?.id)
                        ? 'bg-primary/20 border-primary/50 text-primary'
                        : 'bg-secondary border-border text-slate-400 hover:text-white hover:border-white/20'
                    }`}
                  >
                    {votingId === request.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <ThumbsUp className="w-5 h-5" />
                    )}
                    <span className="font-bold text-lg">{request.votes || 0}</span>
                  </button>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading text-xl font-semibold text-white capitalize">
                        {request.software_name}
                      </h3>
                      <Badge className={`${getStatusBadge(request.status)} capitalize`}>
                        {request.status?.replace('_', ' ') || 'open'}
                      </Badge>
                      {index < 3 && (
                        <Badge className="bg-amber-950/30 text-amber-400 border-amber-900/50">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          Top {index + 1}
                        </Badge>
                      )}
                    </div>
                    
                    {request.description && (
                      <p className="text-slate-400 mb-3">{request.description}</p>
                    )}
                    
                    {request.use_case && (
                      <p className="text-sm text-slate-500 mb-3">
                        <span className="text-slate-400">Use case:</span> {request.use_case}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(request.created_at).toLocaleDateString()}
                      </span>
                      {request.api_docs_url && (
                        <a 
                          href={request.api_docs_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <ExternalLink className="w-4 h-4" />
                          API Docs
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-card border-border sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Request an Integration</DialogTitle>
            <DialogDescription className="text-slate-400">
              Tell us what software you want to connect to your AI agents
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">
                Software Name *
              </label>
              <Input
                value={formData.software_name}
                onChange={(e) => setFormData(prev => ({ ...prev, software_name: e.target.value }))}
                placeholder="e.g., Monday.com, Notion, Airtable"
                required
                className="bg-secondary border-border"
                data-testid="request-software-name"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">
                Description
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What does this software do?"
                className="bg-secondary border-border min-h-20"
                data-testid="request-description"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">
                Your Use Case
              </label>
              <Textarea
                value={formData.use_case}
                onChange={(e) => setFormData(prev => ({ ...prev, use_case: e.target.value }))}
                placeholder="How would you use this integration with AI agents?"
                className="bg-secondary border-border min-h-20"
                data-testid="request-use-case"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">
                API Documentation URL
              </label>
              <Input
                value={formData.api_docs_url}
                onChange={(e) => setFormData(prev => ({ ...prev, api_docs_url: e.target.value }))}
                placeholder="https://docs.example.com/api"
                type="url"
                className="bg-secondary border-border"
                data-testid="request-api-docs"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-slate-700"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 glow-primary bg-primary hover:bg-primary/90"
                disabled={submitting}
                data-testid="submit-request-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestsPage;
